import React from 'react';
import { CardsSection } from 'components/layout/Section/CardsSection';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { NsKey } from 'enums/NsKey';
import { ContractualizationResponseDto } from 'api/quote';

interface QuoteDetailsProps {
  quote: ContractualizationResponseDto;
}

export const QuoteDetails: React.FC<QuoteDetailsProps> = ({ quote }) => {
  const { t } = useDynamicTranslation([NsKey.Common, NsKey.ActorForm, NsKey.Pricing])
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
    }).format(price);
  };

  return (
    <div className="space-y-6">
      <CardsSection title={t('COMMON.LABELS.INSURED_INFOS')}>
        <div className='p-4 rounded-xl border bg-white'>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="text-sm font-medium text-gray-500">{t('ACTOR_FORM.PERSONAL_INFOS.LABELS.FULL_NAME')}</span>
              <p className="text-sm text-gray-900 font-medium">
                {quote.insured.displayName}
              </p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">{t("ACTOR_FORM.PERSONAL_INFOS.LABELS.EMAIL")}</span>
              <p className="text-sm text-gray-900">{quote.insured.email}</p>
            </div>
          </div>
        </div>
      </CardsSection>

      {quote.pricing && (
        <CardsSection title={t('COMMON.LABELS.PRICING_DETAILS')}>
          <div className='p-4 rounded-xl border bg-white'>

            {/* Prix principaux */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="text-center p-4 bg-white rounded-xl border">
                <span className="text-sm font-medium text-gray-500">{t('PRICING.LABELS.PRICE_EXCL_TAX')}</span>
                <p className="text-xl font-bold text-gray-900">
                  {formatPrice(quote.pricing.totalPriceHT)}
                </p>
              </div>
              <div className="text-center p-4 bg-white rounded-xl border">
                <span className="text-sm font-medium text-gray-500">{t('PRICING.LABELS.PRICE_INCL_TAX')}</span>
                <p className="text-xl font-bold text-gray-900">
                  {formatPrice(quote.pricing.totalPriceTTC)}
                </p>
              </div>
              <div className="text-center p-4 bg-primary text-white rounded-xl">
                <span className="text-sm font-medium opacity-90">{t('PRICING.LABELS.CLIENT_PRICE')}</span>
                <p className="text-xl font-bold">
                  {formatPrice(quote.pricing.clientPrice)}
                </p>
              </div>
            </div>

            {/* Frais */}
            <div className="border-t pt-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-500">{t('PRICING.LABELS.TOTAL_FEES_INCL_TAX')}</span>
                <span className="text-sm font-semibold text-gray-900">
                  {formatPrice(quote.pricing.totalFeesTTC)}
                </span>
              </div>
            </div>

            {/* Garanties */}
            {quote.pricing.guarantees && quote.pricing.guarantees.length > 0 && (
              <div className="mt-6">
                <h4 className="text-md font-medium text-gray-900 mb-3">{t('PRICING.GUARANTEES.GUARANTEE')}</h4>
                <div className="space-y-2">
                  {quote.pricing.guarantees.map((guarantee, index) => (
                    <div key={index} className="flex justify-between items-center py-2 px-3 bg-white rounded-lg border">
                      <span className="text-sm text-gray-700">{guarantee.label || `Garantie ${index + 1}`}</span>
                      <span className="text-sm font-medium text-gray-900">
                        {guarantee.priceTTC ? formatPrice(guarantee.priceTTC) : 'Inclus'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Frais détaillés */}
            {quote.pricing.fees && quote.pricing.fees.length > 0 && (
              <div className="mt-6">
                <h4 className="text-md font-medium text-gray-900 mb-3">{t('PRICING.LABELS.FEES_DETAILS')}</h4>
                <div className="space-y-2">
                  {quote.pricing.fees.map((fee, index) => (
                    <div key={index} className="flex justify-between items-center py-2 px-3 bg-white rounded border">
                      <span className="text-sm text-gray-700">{fee.label || `Frais ${index + 1}`}</span>
                      <span className="text-sm font-medium text-gray-900">
                        {fee.priceTTC ? formatPrice(fee.priceTTC) : 'N/A'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardsSection>
      )}
    </div>
  );
};
